import { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import topoData from "@/lib/india-states.topo.json";

// 5-step cyan intensity ramp (light → dark)
const INTENSITY_STEPS = ["#B2E8E5", "#8CD9D4", "#66CAC3", "#40BBB2", "#32B4AC"];
const EMPTY_COLOR = "#E8E8E8";

// Rank circle colors for Top 5 States
const RANK_COLORS = ["#F59E0B", "#32B4AC", "#EF4444", "#9CA3AF", "#D1D5DB"];

const formatMoney = (v) => {
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
};

// Canonical topojson state names (for mapping)
const CANONICAL_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir",
  "Ladakh", "Lakshadweep", "Puducherry",
];

// Alias map: every variant (lowercase) → canonical topojson name
const STATE_ALIASES = {
  // Common misspellings
  "kerela": "Kerala",
  "tamilnadu": "Tamil Nadu",
  "tamil naidu": "Tamil Nadu",
  "orissa": "Odisha",
  "pondicherry": "Puducherry",
  "uttaranchal": "Uttarakhand",
  "nct of delhi": "Delhi",
  "delhi ncr": "Delhi",
  "new delhi": "Delhi",
  "jammu & kashmir": "Jammu and Kashmir",
  "jammu and kashmir": "Jammu and Kashmir",
  "jammu kashmir": "Jammu and Kashmir",
  "j&k": "Jammu and Kashmir",
  "daman and diu": "Dadra and Nagar Haveli and Daman and Diu",
  "dadra and nagar haveli": "Dadra and Nagar Haveli and Daman and Diu",
  "andaman & nicobar": "Andaman and Nicobar Islands",
  "andaman and nicobar": "Andaman and Nicobar Islands",
  "a & n islands": "Andaman and Nicobar Islands",
  "andhrapradesh": "Andhra Pradesh",
  "madhyapradesh": "Madhya Pradesh",
  "himachalpradesh": "Himachal Pradesh",
  "arunachalpradesh": "Arunachal Pradesh",
  "uttarpradesh": "Uttar Pradesh",
  "westbengal": "West Bengal",
};

// Build case-insensitive canonical lookup
const CANON_LC = {};
CANONICAL_STATES.forEach((s) => { CANON_LC[s.toLowerCase()] = s; });

// City → State fallback (when seller.state is blank or unrecognized)
const CITY_TO_STATE = {
  // Uttar Pradesh
  "lucknow": "Uttar Pradesh", "kanpur": "Uttar Pradesh", "agra": "Uttar Pradesh",
  "varanasi": "Uttar Pradesh", "noida": "Uttar Pradesh", "ghaziabad": "Uttar Pradesh",
  "meerut": "Uttar Pradesh", "allahabad": "Uttar Pradesh", "prayagraj": "Uttar Pradesh",
  "bareilly": "Uttar Pradesh", "aligarh": "Uttar Pradesh", "moradabad": "Uttar Pradesh",
  "gorakhpur": "Uttar Pradesh", "mathura": "Uttar Pradesh", "greater noida": "Uttar Pradesh",
  // Maharashtra
  "mumbai": "Maharashtra", "pune": "Maharashtra", "nagpur": "Maharashtra",
  "nashik": "Maharashtra", "thane": "Maharashtra", "aurangabad": "Maharashtra",
  "solapur": "Maharashtra", "kolhapur": "Maharashtra", "navi mumbai": "Maharashtra",
  // Karnataka
  "bengaluru": "Karnataka", "bangalore": "Karnataka", "mysuru": "Karnataka",
  "mysore": "Karnataka", "mangaluru": "Karnataka", "mangalore": "Karnataka",
  "hubli": "Karnataka", "belgaum": "Karnataka",
  // Tamil Nadu
  "chennai": "Tamil Nadu", "coimbatore": "Tamil Nadu", "madurai": "Tamil Nadu",
  "tiruchirappalli": "Tamil Nadu", "salem": "Tamil Nadu", "tirunelveli": "Tamil Nadu",
  // Telangana
  "hyderabad": "Telangana", "warangal": "Telangana", "nizamabad": "Telangana",
  // Delhi
  "delhi": "Delhi", "new delhi": "Delhi",
  // West Bengal
  "kolkata": "West Bengal", "howrah": "West Bengal", "durgapur": "West Bengal",
  "siliguri": "West Bengal", "asansol": "West Bengal",
  // Gujarat
  "ahmedabad": "Gujarat", "surat": "Gujarat", "vadodara": "Gujarat", "rajkot": "Gujarat",
  "gandhinagar": "Gujarat", "bhavnagar": "Gujarat", "jamnagar": "Gujarat",
  // Rajasthan
  "jaipur": "Rajasthan", "jodhpur": "Rajasthan", "udaipur": "Rajasthan",
  "kota": "Rajasthan", "ajmer": "Rajasthan", "bikaner": "Rajasthan",
  // Punjab
  "ludhiana": "Punjab", "amritsar": "Punjab", "jalandhar": "Punjab", "patiala": "Punjab",
  // Haryana
  "gurgaon": "Haryana", "gurugram": "Haryana", "faridabad": "Haryana",
  "chandigarh": "Chandigarh", "panipat": "Haryana", "karnal": "Haryana",
  // Kerala
  "kochi": "Kerala", "cochin": "Kerala", "thiruvananthapuram": "Kerala",
  "trivandrum": "Kerala", "kozhikode": "Kerala", "calicut": "Kerala",
  "thrissur": "Kerala", "kollam": "Kerala", "kannur": "Kerala",
  // Madhya Pradesh
  "bhopal": "Madhya Pradesh", "indore": "Madhya Pradesh", "jabalpur": "Madhya Pradesh",
  "gwalior": "Madhya Pradesh", "ujjain": "Madhya Pradesh",
  // Andhra Pradesh
  "vijayawada": "Andhra Pradesh", "visakhapatnam": "Andhra Pradesh",
  "guntur": "Andhra Pradesh", "tirupati": "Andhra Pradesh",
  // Bihar
  "patna": "Bihar", "gaya": "Bihar", "muzaffarpur": "Bihar", "bhagalpur": "Bihar",
  // Odisha
  "bhubaneswar": "Odisha", "cuttack": "Odisha", "rourkela": "Odisha",
  // Assam
  "guwahati": "Assam", "silchar": "Assam", "dibrugarh": "Assam",
  // Jharkhand
  "ranchi": "Jharkhand", "jamshedpur": "Jharkhand", "dhanbad": "Jharkhand",
  "bokaro": "Jharkhand",
  // Chhattisgarh
  "raipur": "Chhattisgarh", "bhilai": "Chhattisgarh", "bilaspur": "Chhattisgarh",
  // Uttarakhand
  "dehradun": "Uttarakhand", "haridwar": "Uttarakhand", "rishikesh": "Uttarakhand",
  // Himachal Pradesh
  "shimla": "Himachal Pradesh", "manali": "Himachal Pradesh",
  // Goa
  "panaji": "Goa", "panjim": "Goa", "margao": "Goa", "vasco": "Goa",
};

// Normalize a state-ish string to a canonical topojson state name. Returns null if unknown.
const normalizeState = (raw) => {
  if (!raw) return null;
  const t = String(raw).trim();
  if (!t) return null;
  const lc = t.toLowerCase();
  // 1) Direct lowercase canonical lookup (handles UPPERCASE, MixedCase, lowercase input)
  if (CANON_LC[lc]) return CANON_LC[lc];
  // 2) Known aliases/misspellings
  if (STATE_ALIASES[lc]) return STATE_ALIASES[lc];
  // 3) Strip punctuation + extra spaces then retry
  const clean = lc.replace(/[.,&]/g, " ").replace(/\s+/g, " ").trim();
  if (CANON_LC[clean]) return CANON_LC[clean];
  if (STATE_ALIASES[clean]) return STATE_ALIASES[clean];
  // 4) Title-case attempt (for inputs like "ladakh ut" etc.)
  return null;
};

// Resolve seller → canonical state. Tries state first, then city fallback.
const resolveState = (seller) => {
  if (!seller) return null;
  const stateNorm = normalizeState(seller.state);
  if (stateNorm) return stateNorm;
  // City fallback
  const city = (seller.city || "").trim().toLowerCase();
  if (city && CITY_TO_STATE[city]) return CITY_TO_STATE[city];
  return null;
};

export default function IndiaSalesHeatmap({ quotations = [], sellerMap = {} }) {
  const [hoverState, setHoverState] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  // Aggregate sales by canonical state
  const { stateTotals, totalRevenue, maxRevenue } = useMemo(() => {
    const totals = {};
    let grand = 0;
    quotations.forEach((q) => {
      const seller = sellerMap[q.seller_id];
      const st = resolveState(seller);
      if (!st) return;
      const rev = q.total_invoice_value || 0;
      const units = (q.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
      if (!totals[st]) totals[st] = { revenue: 0, units: 0 };
      totals[st].revenue += rev;
      totals[st].units += units;
      grand += rev;
    });
    const max = Object.values(totals).reduce((m, v) => Math.max(m, v.revenue), 0);
    return { stateTotals: totals, totalRevenue: grand, maxRevenue: max };
  }, [quotations, sellerMap]);

  const top5 = useMemo(() => {
    return Object.entries(stateTotals)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [stateTotals]);

  const getColor = (stateName) => {
    const entry = stateTotals[stateName];
    if (!entry || entry.revenue <= 0) return EMPTY_COLOR;
    if (maxRevenue <= 0) return EMPTY_COLOR;
    const ratio = entry.revenue / maxRevenue;
    // 5 buckets: [0, 0.2) → step0, [0.2, 0.4) → step1, ..., [0.8, 1.0] → step4
    const idx = Math.min(INTENSITY_STEPS.length - 1, Math.floor(ratio * INTENSITY_STEPS.length));
    return INTENSITY_STEPS[idx];
  };

  const tooltipState = hoverState?.name || selectedState;
  const tooltipData = tooltipState ? stateTotals[tooltipState] : null;
  const tooltipShare = tooltipData && totalRevenue > 0 ? (tooltipData.revenue / totalRevenue) * 100 : 0;

  return (
    <div className="bg-white border border-[#E8E8E8] rounded-lg p-5" data-testid="india-sales-heatmap">
      <h2 className="text-base font-semibold text-[#0F0F0F] mb-4">India Sales Heatmap</h2>

      <div className="grid grid-cols-[200px_1fr] gap-6 items-start">
        {/* LEFT: Top 5 States list */}
        <div>
          <h3 className="text-sm font-bold text-[#0F0F0F] mb-3">Top 5 States</h3>
          {top5.length === 0 ? (
            <p className="text-xs text-muted-foreground">No data</p>
          ) : (
            <ul className="space-y-2">
              {top5.map((s, i) => (
                <li
                  key={s.name}
                  onClick={() => setSelectedState(s.name)}
                  className={`flex items-center gap-2.5 cursor-pointer py-1 px-1.5 rounded transition-colors ${
                    selectedState === s.name ? "bg-[#E1F5EE]" : "hover:bg-slate-50"
                  }`}
                  data-testid={`top-state-${i + 1}`}
                >
                  <span
                    className="flex-shrink-0 w-5 h-5 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                    style={{ background: RANK_COLORS[i] }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-xs text-[#1A1A1A] truncate">{s.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* RIGHT: Map + Legend */}
        <div className="relative pt-10">
          <div className="w-full" style={{ height: 560 }}>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 1000, center: [82, 23] }}
              width={600}
              height={620}
              style={{ width: "100%", height: "100%" }}
            >
              <Geographies geography={topoData}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const stName = geo.properties?.st_nm || "";
                    const fill = getColor(stName);
                    const isHovered = hoverState?.name === stName || selectedState === stName;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke="#FFFFFF"
                        strokeWidth={isHovered ? 1.2 : 0.5}
                        onMouseEnter={(e) =>
                          setHoverState({ name: stName, x: e.clientX, y: e.clientY })
                        }
                        onMouseMove={(e) =>
                          setHoverState({ name: stName, x: e.clientX, y: e.clientY })
                        }
                        onMouseLeave={() => setHoverState(null)}
                        onClick={() => setSelectedState(stName)}
                        style={{
                          default: { outline: "none", transition: "opacity 0.15s, stroke-width 0.15s" },
                          hover: { outline: "none", opacity: 0.85, cursor: "pointer" },
                          pressed: { outline: "none" },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>
          </div>

          {/* Tooltip card */}
          {tooltipState && tooltipData && (
            <div
              className="absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-[#E8E8E8] p-3 min-w-[180px] pointer-events-none"
              data-testid="heatmap-tooltip"
              style={{ zIndex: 10 }}
            >
              <p className="text-sm font-bold text-[#0F0F0F] mb-1.5">{tooltipState}</p>
              <div className="space-y-0.5 text-xs">
                <div className="flex justify-between gap-3">
                  <span className="text-[#1A1A1A]/70">Sales:</span>
                  <span className="font-medium" style={{ color: "#32B4AC" }}>
                    {formatMoney(tooltipData.revenue)}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[#1A1A1A]/70">Units:</span>
                  <span className="font-medium text-[#0F0F0F]">
                    {tooltipData.units.toLocaleString("en-IN")}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[#1A1A1A]/70">Share:</span>
                  <span className="font-medium" style={{ color: "#32B4AC" }}>
                    {tooltipShare.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Legend (bottom right) */}
          <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm rounded-md border border-[#E8E8E8] px-2.5 py-1.5">
            <p className="text-[10px] font-medium text-[#1A1A1A] mb-1">Sales Intensity</p>
            <div className="flex items-center gap-1">
              <span className="text-[9px] text-[#1A1A1A]/60">Low</span>
              <div className="flex">
                {INTENSITY_STEPS.map((c, i) => (
                  <div key={i} className="w-4 h-2.5" style={{ background: c }} />
                ))}
              </div>
              <span className="text-[9px] text-[#1A1A1A]/60">High</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
